import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "./nativeHttp";

export const RESERVED_BUSINESS_SLUGS = new Set([
  "admin",
  "api",
  "app",
  "auth",
  "booking",
  "business",
  "businesses",
  "calendar",
  "customers",
  "edit",
  "events",
  "help",
  "login",
  "logout",
  "new-business",
  "owner-businesses",
  "privacy",
  "profile",
  "register",
  "settings",
  "signup",
  "soporte",
  "support",
  "terms",
]);

export function slugifyBusinessName(value = "") {
  const base = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " y ")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .toLowerCase();

  const clean = base || "negocio";
  return RESERVED_BUSINESS_SLUGS.has(clean) ? `${clean}-loop` : clean.slice(0, 72).replace(/-+$/g, "");
}

export function getBusinessRouteParam(business = {}) {
  const rawSlug = business?.slug || business?.publicSlug || business?.businessSlug || business?.slugLower || "";
  const slug = slugifyBusinessName(rawSlug);
  if (slug && slug !== "negocio") return slug;
  return String(business?.id || business?.businessId || business?.uid || "").trim();
}

export function businessPublicPath(business = {}) {
  const param = getBusinessRouteParam(business);
  return param ? `/business/${encodeURIComponent(param)}` : "/business";
}

export function businessAdminPath(business = {}, section = "calendar") {
  const param = getBusinessRouteParam(business);
  const cleanSection = String(section || "calendar").replace(/^\/+|\/+$/g, "") || "calendar";
  return param ? `/business/${encodeURIComponent(param)}/${cleanSection}` : `/${cleanSection}`;
}

function getSlugFunctionsInstance() {
  return getFunctions();
}


function getBusinessSlugCandidates(business = {}) {
  const candidates = [
    business?.slugLower,
    business?.slug,
    business?.publicSlug,
    business?.businessSlug,
  ];
  return candidates
    .map((value) => slugifyBusinessName(value || ""))
    .filter(Boolean);
}

function businessMatchesSlug(business = {}, slug = "") {
  if (!business || !slug) return false;
  return getBusinessSlugCandidates(business).includes(slug);
}

async function getBusinessDocNativeSafe(db, businessId) {
  try {
    if (!db || !businessId || typeof fsClient.getDocByPath !== "function") return null;
    const data = await fsClient.getDocByPath(`businesses/${businessId}`, { db });
    if (!data || typeof data !== "object") return null;
    return { id: data.id || businessId, ...data };
  } catch (_) {
    return null;
  }
}

async function listBusinessesNativeSafe(db) {
  try {
    if (!db || typeof fsClient.listCollectionByPath !== "function") return [];
    const docs = await fsClient.listCollectionByPath("businesses", {}, { db });
    return (Array.isArray(docs) ? docs : [])
      .map((item) => ({ id: item?.id || item?.businessId || item?.uid || "", ...(item || {}) }))
      .filter((item) => item && item.id);
  } catch (_) {
    return [];
  }
}

async function callReserveBusinessSlug(payload) {
  const fn = httpsCallable(getSlugFunctionsInstance(), "reserveBusinessSlug");
  const result = await fn(payload);
  return result && result.data ? result.data : {};
}

async function callBackfillBusinessSlugs(payload) {
  const fn = httpsCallable(getSlugFunctionsInstance(), "backfillBusinessSlugs");
  const result = await fn(payload || {});
  return result && result.data ? result.data : {};
}

export async function findBusinessBySlug(db, rawSlug) {
  const slug = slugifyBusinessName(rawSlug);
  if (!db || !slug) return null;

  // iOS/Capacitor: avoid Firestore Web SDK queries inside WKWebView.
  // Some public routes call this helper before auth is fully ready; REST/native-safe reads
  // keep BusinessPage and Header navigation working as public browsing.
  if (isIosCapacitor()) {
    const all = await listBusinessesNativeSafe(db);
    return all.find((business) => businessMatchesSlug(business, slug)) || null;
  }

  const attempts = [
    query(collection(db, "businesses"), where("slugLower", "==", slug), limit(1)),
    query(collection(db, "businesses"), where("slug", "==", slug), limit(1)),
    query(collection(db, "businesses"), where("publicSlug", "==", slug), limit(1)),
    query(collection(db, "businesses"), where("businessSlug", "==", slug), limit(1)),
  ];

  for (const q of attempts) {
    const snap = await getDocs(q);
    if (!snap.empty) {
      const first = snap.docs[0];
      return { id: first.id, ...(first.data() || {}) };
    }
  }

  return null;
}

export async function resolveBusinessIdentifier(db, identifier) {
  const raw = String(identifier || "").trim();
  if (!db || !raw) return { businessId: null, business: null, slug: "" };

  try {
    if (isIosCapacitor()) {
      const direct = await getBusinessDocNativeSafe(db, raw);
      if (direct && direct.id) {
        return {
          businessId: direct.id,
          business: direct,
          slug: direct.slug || direct.publicSlug || direct.businessSlug || direct.slugLower || "",
          routeParam: getBusinessRouteParam(direct),
          resolvedBy: "id-native",
        };
      }
    } else {
      const directRef = doc(db, "businesses", raw);
      const directSnap = await getDoc(directRef);
      if (directSnap.exists()) {
        const data = directSnap.data() || {};
        return {
          businessId: directSnap.id,
          business: { id: directSnap.id, ...data },
          slug: data.slug || data.publicSlug || data.businessSlug || data.slugLower || "",
          routeParam: getBusinessRouteParam({ id: directSnap.id, ...data }),
          resolvedBy: "id",
        };
      }
    }
  } catch (_) {}

  const bySlug = await findBusinessBySlug(db, raw);
  if (bySlug && bySlug.id) {
    return {
      businessId: bySlug.id,
      business: bySlug,
      slug: bySlug.slug || bySlug.publicSlug || bySlug.businessSlug || bySlug.slugLower || slugifyBusinessName(raw),
      routeParam: getBusinessRouteParam(bySlug),
      resolvedBy: "slug",
    };
  }

  return { businessId: raw, business: null, slug: slugifyBusinessName(raw), routeParam: slugifyBusinessName(raw), resolvedBy: "fallback" };
}

export async function isBusinessSlugAvailable(db, slug, excludeBusinessId = null) {
  const clean = slugifyBusinessName(slug);
  if (!db || !clean || RESERVED_BUSINESS_SLUGS.has(clean)) return false;

  const found = await findBusinessBySlug(db, clean);
  if (!found) return true;
  return excludeBusinessId && String(found.id) === String(excludeBusinessId);
}

export async function generateUniqueBusinessSlug(db, name, excludeBusinessId = null) {
  const base = slugifyBusinessName(name);
  let candidate = base;
  for (let i = 2; i <= 60; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const available = await isBusinessSlugAvailable(db, candidate, excludeBusinessId);
    if (available) return candidate;
    candidate = `${base}-${i}`;
  }
  return `${base}-${Date.now().toString(36)}`;
}


export async function reserveBusinessSlug(db, slug, businessId, meta = {}) {
  const clean = slugifyBusinessName(slug);
  const id = String(businessId || "").trim();
  if (!id || !clean || clean === "negocio") {
    const err = new Error("URL pública inválida");
    err.code = "invalid-slug";
    throw err;
  }

  const data = await callReserveBusinessSlug({
    businessId: id,
    desiredSlug: clean,
    name: meta.businessName || meta.name || clean,
    updateBusiness: meta.updateBusiness !== false,
    mode: "exact",
  });

  return data.slug || clean;
}

export async function reserveUniqueBusinessSlug(db, name, businessId, meta = {}) {
  const id = String(businessId || "").trim();
  const base = slugifyBusinessName(meta.desiredSlug || name || "negocio");
  if (!id || !base || base === "negocio") {
    const err = new Error("URL pública inválida");
    err.code = "invalid-slug";
    throw err;
  }

  const data = await callReserveBusinessSlug({
    businessId: id,
    desiredSlug: base,
    name: meta.businessName || meta.name || name || base,
    updateBusiness: meta.updateBusiness !== false,
    mode: "unique",
  });

  return data.slug || base;
}

export async function backfillMissingBusinessSlugs(db, options = {}) {
  const data = await callBackfillBusinessSlugs({
    dryRun: options.dryRun === true,
    limit: options.limit || 200,
  });
  return Array.isArray(data.results) ? data.results : [];
}
